import { useState, useMemo } from 'react';
import Badge from './Badge';
import Dropdown from './Dropdown';
import { getThemeBadgeVariant } from '../../constants/subThemes';

/**
 * AccessSelector - Beautiful shared component for selecting roles and products/users with access levels
 *
 * @param {Array} roles - Array of role objects {id, name, theme}
 * @param {Array} items - Array of product/user objects {id, name, version?, full_name?}
 * @param {Set} selectedRoles - Set of selected role IDs
 * @param {Set} selectedItems - Set of selected product/user IDs
 * @param {Object} itemAccessLevels - Object mapping item ID to access level
 * @param {Function} onRoleToggle - Callback when role is toggled
 * @param {Function} onItemToggle - Callback when item is toggled
 * @param {Function} onItemAccessLevelChange - Callback when item access level changes
 * @param {string} itemLabel - Label for items tab (e.g., "Products" or "Users")
 * @param {boolean} disabled - Whether selectors are disabled
 */
export default function AccessSelector({
  roles = [],
  items = [],
  selectedRoles,
  selectedItems,
  itemAccessLevels = {},
  onRoleToggle,
  onItemToggle,
  onItemAccessLevelChange,
  itemLabel = 'Items',
  disabled = false
}) {
  const [activeTab, setActiveTab] = useState('roles');
  const [searchQuery, setSearchQuery] = useState('');

  // Filter items based on search
  const filteredRoles = useMemo(() => {
    if (!searchQuery) return roles;
    const query = searchQuery.toLowerCase();
    return roles.filter(role => role.name.toLowerCase().includes(query));
  }, [roles, searchQuery]);

  const filteredItems = useMemo(() => {
    if (!searchQuery) return items;
    const query = searchQuery.toLowerCase();
    return items.filter(item => {
      const name = item.name?.toLowerCase() || '';
      const fullName = item.full_name?.toLowerCase() || '';
      const username = item.username?.toLowerCase() || '';
      return name.includes(query) || fullName.includes(query) || username.includes(query);
    });
  }, [items, searchQuery]);

  const handleSelectAll = () => {
    if (activeTab === 'roles') {
      filteredRoles.forEach(role => {
        if (!selectedRoles.has(role.id)) {
          onRoleToggle(role.id);
        }
      });
    } else {
      filteredItems.forEach(item => {
        if (!selectedItems.has(item.id)) {
          onItemToggle(item.id);
        }
      });
    }
  };

  const handleClearAll = () => {
    if (activeTab === 'roles') {
      filteredRoles.forEach(role => {
        if (selectedRoles.has(role.id)) {
          onRoleToggle(role.id);
        }
      });
    } else {
      filteredItems.forEach(item => {
        if (selectedItems.has(item.id)) {
          onItemToggle(item.id);
        }
      });
    }
  };

  const accessLevelOptions = [
    { value: 'read', label: 'Read' },
    { value: 'write', label: 'Write' },
    { value: 'admin', label: 'Admin' }
  ];

  const selectedRolesCount = selectedRoles.size;
  const selectedItemsCount = selectedItems.size;
  const isRolesTab = activeTab === 'roles';
  const currentFilteredCount = isRolesTab ? filteredRoles.length : filteredItems.length;
  const currentSelectedCount = isRolesTab ? selectedRolesCount : selectedItemsCount;
  const currentTotalCount = isRolesTab ? roles.length : items.length;

  return (
    <div className="border border-gray-600/50 rounded-lg bg-gradient-to-br from-gray-800/40 to-gray-800/20 backdrop-blur-sm" style={{ color: 'var(--color-text-on-accent)' }}>
          {/* Header */}
      <div className="p-4 border-b border-gray-700/50">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/10 rounded-lg border border-purple-500/20" style={{ color: 'var(--color-text-on-accent)' }}>
                          <svg className="w-4 h-4 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">Access Control</h3>
              <p className="text-xs text-gray-400">
                {currentSelectedCount} of {currentTotalCount} selected
              </p>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex gap-2">
            <button
              onClick={handleSelectAll}
              disabled={disabled || currentFilteredCount === 0}
              className="px-3 py-1.5 text-xs font-medium bg-purple-600/10 hover:bg-purple-600/20 text-purple-300 border border-purple-500/20 rounded-md transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:border-purple-500/30"
              style={{ color: 'var(--color-text-on-accent)' }}
            >
              <div className="flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Select All
              </div>
            </button>
            <button
              onClick={handleClearAll}
              disabled={disabled || currentSelectedCount === 0}
              className="px-3 py-1.5 text-xs font-medium bg-gray-700/30 hover:bg-gray-700/50 text-gray-300 border border-gray-600/30 rounded-md transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:border-gray-600/50"
            >
              <div className="flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Clear All
              </div>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2">
          <div className="flex-1 inline-flex gap-1 bg-gray-900/50 p-1 rounded-lg border border-gray-700/50">
            <button
              onClick={() => {
                setActiveTab('roles');
                setSearchQuery('');
              }}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all ${
                isRolesTab
                  ? 'bg-purple-600 shadow-lg shadow-purple-900/20'
                  : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800/50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                <span>Roles</span>
                {selectedRolesCount > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 bg-purple-500/20 text-purple-300 text-xs rounded-full font-semibold" style={{ color: 'var(--color-text-on-accent)' }}>
                                      {selectedRolesCount}
                  </span>
                )}
              </div>
            </button>
            <button
              onClick={() => {
                setActiveTab('items');
                setSearchQuery('');
              }}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all ${
                !isRolesTab
                  ? 'bg-purple-600 shadow-lg shadow-purple-900/20'
                  : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800/50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                <span>{itemLabel}</span>
                {selectedItemsCount > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 bg-purple-500/20 text-purple-300 text-xs rounded-full font-semibold" style={{ color: 'var(--color-text-on-accent)' }}>
                                      {selectedItemsCount}
                  </span>
                )}
              </div>
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mt-3 relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={`Search ${isRolesTab ? 'roles' : itemLabel.toLowerCase()}...`}
            className="w-full pl-10 pr-10 py-2.5 bg-gray-900/50 border border-gray-700/50 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500/50 transition-all"
          />
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Filter Info */}
        {searchQuery && (
          <div className="mt-2 text-xs text-gray-400">
            Showing {currentFilteredCount} of {currentTotalCount} {isRolesTab ? 'roles' : itemLabel.toLowerCase()}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="h-64 overflow-y-auto border border-gray-700/30 rounded-lg bg-gray-900/30 custom-scrollbar">
          {isRolesTab ? (
            filteredRoles.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-gray-400 p-8">
                <svg className="w-12 h-12 mb-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <p className="text-sm font-medium">{searchQuery ? 'No roles found' : 'No roles available'}</p>
                {searchQuery && (
                  <p className="text-xs mt-1">Try a different search term</p>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2 p-3">
                {filteredRoles.map((role) => {
                  const isSelected = selectedRoles.has(role.id);
                  return (
                    <div
                      key={role.id}
                      onClick={() => !disabled && onRoleToggle(role.id)}
                      className={`group flex items-center gap-2.5 p-3 rounded-lg border cursor-pointer transition-all ${
                        isSelected
                          ? 'bg-purple-500/10 border-purple-500/40 shadow-sm shadow-purple-900/10 ring-1 ring-purple-500/20'
                          : 'bg-gray-800/40 border-gray-700/40 hover:border-gray-600/60 hover:bg-gray-800/60'
                      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                        isSelected
                          ? 'bg-purple-500 border-purple-500'
                          : 'border-gray-600 group-hover:border-gray-500'
                      }`}>
                        {isSelected && (
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <Badge variant={getThemeBadgeVariant(role.theme)} className="text-xs flex-1">
                        {role.name}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            filteredItems.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-gray-400 p-8">
                <svg className="w-12 h-12 mb-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                <p className="text-sm font-medium">{searchQuery ? `No ${itemLabel.toLowerCase()} found` : `No ${itemLabel.toLowerCase()} available`}</p>
                {searchQuery && (
                  <p className="text-xs mt-1">Try a different search term</p>
                )}
              </div>
            ) : (
              <div className="divide-y divide-gray-700/20">
                {filteredItems.map((item) => {
                  const isSelected = selectedItems.has(item.id);
                  return (
                    <div
                      key={item.id}
                      className={`group flex items-center justify-between gap-4 px-4 py-3.5 transition-all ${
                        isSelected
                          ? 'bg-purple-500/5 hover:bg-purple-500/8'
                          : 'hover:bg-gray-800/40'
                      }`}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div
                          onClick={() => !disabled && onItemToggle(item.id)}
                          className={`cursor-pointer flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                            isSelected
                              ? 'bg-purple-500 border-purple-500'
                              : 'border-gray-600 group-hover:border-gray-500'
                          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {isSelected && (
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-white text-sm font-medium truncate">{item.name || item.username}</div>
                          {(item.version || item.full_name) && (
                            <div className="text-xs text-gray-400 truncate">
                              {item.version ? `v${item.version}` : item.full_name}
                            </div>
                          )}
                        </div>
                      </div>
                      {isSelected && (
                        <div className="flex-shrink-0 animate-fadeIn">
                          <Dropdown
                            value={itemAccessLevels[item.id] || 'read'}
                            onChange={(e) => onItemAccessLevelChange(item.id, e.target.value)}
                            options={accessLevelOptions}
                            disabled={disabled}
                            className="text-xs w-28"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
